class StateHandler extends HTMLElement {
  useProvider = true;
  constructor() {
    super();
    // Estado del componente
    this.__internalState = {};
    this.__subscribers = [];
  }
  getState() {
    return this.__internalState;
  }

  // Al renderizar
  connectedCallback() {
    if (!this.isConnected) return;
    if (super.connectedCallback) super.connectedCallback();
    if (this.useProvider === false) return;
    this.provider = this.closest(this.tagName === 'H-REPEATER' ? 'h-provider' : 'h-repeater, h-provider');
    if (this.provider) this.provider.register(this);
  }

  // Cambio de estado
  setState(newState = {}) {
    this.__internalState = { ...this.__internalState, ...newState };
    if (this.onUpdate) this.onUpdate(this.__internalState);
    this.__subscribers.forEach((c) => c(this.__internalState));
  }

  // Suscribirse al cambio de estado
  subscribe(callback) {
    this.__subscribers = [...this.__subscribers, callback];
  }

  // Crear propiedades reactivas
  makeState(name, defaultValue) {
    const TS = this;
    if (this.__internalState[name]) return;
    this.__internalState[name] = defaultValue;
    Object.defineProperty(this, name, {
      set: (val) => {
        TS.setState({ [name]: val });
      },
      get: () => {
        return TS.__internalState[name];
      },
    });
  }

  // Crear ID
  makeID() {
    return 'c-' + Math.floor(Math.random() * 999999);
  }

  // Setear al estado global
  globalSetter(id, value) {
    if (!this.provider) return console.error('No hay un provider');
    this.provider.setter(id, value);
  }
}

class HElement extends StateHandler {
  constructor() {
    super();
    const attrs = this.getAttributes();
    this.shadowDom = attrs['shadow-dom'] === undefined || attrs['shadow-dom'] === 'true';
    if (this.shadowDom) {
      // Encapsulamiento del componente
      this.attachShadow({ mode: 'open' });
      this.root = this.shadowRoot;

      try {
        let indexOf = -1;
        if (this.template) {
          const _html = this.template({});
          indexOf = _html.indexOf('<slot>');
        }

        if (indexOf === -1) {
          const children = this.childNodes;
          this.root.append(...children);
        }
      } catch (err) {
        console.error(err);
      }

      // Seleccionar Elementos
      this.querySelector = (selectors) => {
        return this.root.querySelector(selectors);
      };
      this.querySelectorAll = (selectors) => {
        return this.root.querySelectorAll(selectors);
      };
      this.append = (...elements) => {
        return this.root.append(...elements);
      };
      this.appendChild = (node) => {
        return this.root.appendChild(node);
      };
      this.prepend = (...nodes) => {
        return this.root.prepend(...nodes);
      };
    } else {
      this.root = this;
    }
  }

  // Inicalización del componente
  connectedCallback() {
    if (!this.isConnected) return;
    super.connectedCallback();
    if (this.template) {
      this.root.innerHTML = this.template(this.getAttributes());
    }
    if (this.mount) this.mount(this.root);
  }
  mount() {
    Object.entries(this.getAttributes()).forEach(([k, v]) => {
      this.makeState(k, v);
    });
  }

  // Obtener attributes del elemento
  getAttributes() {
    return HElement.GetAttributes(this);
  }

  // Obtener attributos de cualquier elemento
  static GetAttributes(element) {
    const attributes = element.attributes;
    if (!attributes) return null;
    return Object.assign(
      {},
      ...Array.from(element.attributes, ({ name, value }) => ({
        [name]: value,
      }))
    );
  }
}

class HReactiveElement extends HElement {
  mount() {
    super.mount();
    const reg = /\[([^\]]+)](\(([^)]+)\))?/g;
    const value = '<h-value value="$1">$3</h-value>';
    const paramsReg = /\s([^=]+)="(\[([^\]]+)\])"/g;
    const paramsValue = '$1="@$3" h-reactive="true" ';
    const eventReg = /\s(on-[^=]+)="([^"]+)"/g;
    const eventValue = `$& h-event `;
    this.root.innerHTML = this.root.innerHTML
      .replace(paramsReg, paramsValue)
      .replace(eventReg, eventValue)
      .replace(reg, value);

    /* Setea listeners */
    this.interactiveElements = this.root.querySelectorAll('[h-event]');
    this.interactiveElements.forEach((element) => {
      this.__setReactiveEvents(element);
    });

    /* prepara la reactividad de los values */
    this.reactiveValues = this.root.querySelectorAll('h-value');
    this.reactiveValues.forEach((el) => {
      const attr = el.getAttribute('value');
      if (this[attr]) el.innerHTML = this[attr];
      if (this[attr]) return;
      this.makeState(attr, this[attr] || el.innerHTML);
    });

    /* prepara la reactividad de los parametros */
    this.reactiveAttributes = this.root.querySelectorAll(`[h-reactive="true"]`);
    this.reactiveAttributes.forEach((el) => {
      const params = HElement.GetAttributes(el);
      const attrs = {};
      Object.entries(params).forEach(([key, value]) => {
        if (!value.startsWith('@')) return;
        attrs[key] = value.replace('@', '');
        this.makeState(attrs[key], this[attrs[key]]);
        this.setReactiveAttribute(el, key, attrs[key]);
        /*el.removeAttribute(key);*/
      });
      el.reactiveParentAttributes = attrs;
      el.removeAttribute('h-reactive');
    });

    this.subscribe((state) => {
      this.reactiveValues.forEach((el) => {
        const attr = el.getAttribute('value');
        if (state[attr] === undefined) return;
        el.innerHTML = state[attr];
      });
      this.reactiveAttributes.forEach((el) => {
        Object.entries(el.reactiveParentAttributes).forEach(([attr, value]) => {
          this.setReactiveAttribute(el, attr, value);
        });
      });

      if (this.onUpdateFinished) this.onUpdateFinished(this.__internalState);
    });
  }

  __setReactiveEvents(element) {
    const attributes = HElement.GetAttributes(element);
    if (!attributes) return;

    const keys = Object.keys(attributes).filter((attr) => attr.startsWith('on-'));
    if (!keys) return;

    const eventListener = (attr) => {
      const name = attr.replace('on-', '').toLowerCase();
      const func = (e) => this[attributes[attr]](e);
      if (!func) return false;
      element.addEventListener(name, func);
      element.removeAttribute(attr);
    };
    keys.forEach((attr) => {
      if (attr.startsWith('on-')) return eventListener(attr);
    });
  }

  setReactiveAttribute(el, attr, value) {
    const val = this[value];
    el.setAttribute(attr, val);
    el[attr] = val;
  }
}

class HProvider extends StateHandler {
  useProvider = false;
  isProvider = true;
  styles = [];
  constructor() {
    super();
    this.__HElementsChildren = [];
    this.__HElementsMap = {};
  }
  connectedCallback() {
    return super.connectedCallback();
  }

  onUpdate(state) {
    this.__HElementsChildren.forEach((el) => {
      if (this.changed === el) return;
      if (el.onGlobalUpdate) el.onGlobalUpdate(state);
    });
    this.changed = false;
  }
  register(element) {
    if (this.styles.length === 0) {
      this.styles = [...this.querySelectorAll('style, link')];
    }

    if (!element.id) element.id = this.makeID();
    this.__HElementsChildren = [...this.__HElementsChildren, element];
    this.__HElementsMap = { ...this.__HElementsMap, [element.id]: element };
    this.setState({
      [element.id]: element.getState(),
    });
    element.subscribe((state) => {
      this.changed = element;
      this.setState({
        [element.id]: { ...this.__internalState[element.id], ...state },
      });
    });

    if (element.onGlobalUpdate) element.onGlobalUpdate(this.getState());

    if (this.styles.length === 0) return;
    const styleList = this.styles.map((st) => st.cloneNode(true));
    if (element.isProvider) {
      element.styles = [...element.styles, ...styleList];
    } else {
      element.root.append(...styleList);
    }
  }
  setter(id, value) {
    const component = this.__HElementsChildren.find((el) => el.id === id);
    component.setState({ ...value });
  }
  getComponent(id) {
    return this.__HElementsMap[id];
  }
}
customElements.define('h-provider', HProvider);

class HRepeater extends HProvider {
  componentRef;
  attributeRef;
  logPrefix = '<H-LIST>:';
  listElements = {};
  container;
  component;
  useProvider = true;
  constructor() {
    super();
    this.makeState('listElements', this.listElements);
  }
  connectedCallback() {
    if (!this.isConnected) return;
    super.connectedCallback();
    this.logPrefix = `<H-LIST>(${this.id}):`;

    this.container = document.createElement('div');
    this.component = this.firstElementChild;
    if (this.component) {
      this.component.useProvider = false;
      this.component.remove();
    }
    this.appendChild(this.container);

    const ref = this.getAttribute('ref');
    if (ref === null)
      return console.error(
        `${this.logPrefix} El atributo ref es obligatorio (debe ser [component.id].[listAttribute])`
      );

    const [componentRef, attributeRef] = ref.split('.');
    this.componentRef = componentRef;
    this.attributeRef = attributeRef;
  }
  register(element) {
    if (this.component === null) {
      this.component = element;
      this.component.remove();
    }
  }
  onGlobalUpdate(state) {
    try {
      if (!state[this.componentRef]) return;

      const list = state[this.componentRef][this.attributeRef];
      const ids = list.map((item) => {
        if (!item.id) throw new Error(`${this.logPrefix} Los items del listado deben tener un ID`);
        if (!this.listElements[item.id]) this.makeItem(item);
        this.updateUI(item);
        return item.id;
      });
      Object.keys(this.listElements).forEach((k) => {
        if (ids.includes(k)) return;
        this.listElements[k].remove();
        const { [k]: removed, ...obj } = this.listElements;
        this.listElements = obj;
      });
    } catch (err) {
      console.error(`${this.logPrefix} ${err.message}`);
    }
  }
  makeItem(item) {
    this.listElements = {
      ...this.listElements,
      [item.id]: this.component.cloneNode(true),
    };
  }
  updateUI(item) {
    const element = this.listElements[item.id];
    Object.entries(item).forEach(([key, value]) => {
      element.setAttribute(key, value);
      element[key] = value;
    });
    if (!element.parentElement) this.container.appendChild(element);
  }
  globalSetter(id, value) {
    return this.provider.globalSetter(id, value);
  }
  getState() {
    return this.provider.getState();
  }
  setter(id, value) {
    return this.provider.setter(id, value);
  }
  getComponent(id) {
    return this.provider.getComponent(id);
  }

  removeItem(id) {
    const { [this.componentRef]: ref } = this.provider.getState();
    const list = ref[this.attributeRef];
    const index = list.findIndex((item) => item.id === id);
    if (index === -1) return console.error(`${this.prefix} El item no existe`);
    return this.provider.setter(this.componentRef, {
      [this.attributeRef]: [...list.slice(0, index), ...list.slice(index + 1)],
    });
  }
  updateItem(item) {
    const { [this.componentRef]: ref } = this.provider.getState();
    const list = ref[this.attributeRef];
    const index = list.findIndex((itm) => itm.id === item.id);
    if (index === -1) return console.error(`${this.prefix} El item no existe`);
    return this.provider.setter(this.componentRef, {
      [this.attributeRef]: [...list.slice(0, index), item, ...list.slice(index + 1)],
    });
  }
}
customElements.define('h-repeater', HRepeater);
